import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { useDispatch, useSelector } from "react-redux"
import { slideFromTop, slideFromRight } from "../../ui/animations"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { parseObservationFilters } from "./filters"
import {
  fetchObservationIDs,
  fetchObservationById,
  selectLocationWithinIDs,
  selectUnfetchedPeers,
} from "./slice"
import { updateSearchParamsWithFilters } from "../filters/filters"
import { isImage, preFetchFile } from "./media"
import { selectAuth } from "../iam/slice"
import { useInterval } from "../timing/use-interval"

// Period to re-fetch the currently-browsed index of observations in the background
const LIVE_UPDATE_INTERVAL = 30000 // ms

// TODO: Move to common UI component def for floating app bar
const Bar = styled.div`
  display: flex;
  padding: 1rem;
  background-color: ${(props) => props.theme.colours.paper};
  animation: 0.3s ${slideFromTop} ease-out;
`

// TODO: Same as for `CollectionBar`
const NavContainer = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  * {
    margin-left: 0.5rem;
  }
  input {
    width: 2rem;
  }
  animation: 0.5s ${slideFromRight} ease-out;
`

const Heading = styled.div`
  flex: auto;
`

const useKeyPress = function (targetKey) {
  const [keyPressed, setKeyPressed] = useState(false)

  function downHandler({ key }) {
    if (key === targetKey) {
      setKeyPressed(true)
    }
  }

  const upHandler = ({ key }) => {
    if (key === targetKey) {
      setKeyPressed(false)
    }
  }

  React.useEffect(() => {
    window.addEventListener("keydown", downHandler)
    window.addEventListener("keyup", upHandler)

    return () => {
      window.removeEventListener("keydown", downHandler)
      window.removeEventListener("keyup", upHandler)
    }
  })

  return keyPressed
}

// NavigateWithinObservationsList renders a top nav which, given an observationId and a set of `filter`
// URL query parameters as per `<ObservationsList>`, determines where the current observation lies within
// said collection, and allows for easy forward / backward navigation.
// This supports simple use-cases such as:
// 1. Select a set of observations matching certain filter criteria
// 2. Open the first observation in the `ObservationDetail` view to e.g. perform curation
// 3. Easily and rapidly "step through" all observations in the list.
// TODO: Pre-fetch observation data and files for the next / previous observation, so that the
// user has a smooth navigation experience.
const NavigateWithinObservationsList = ({ observationId }) => {
  // Current observation (note: Probably not needed)
  //const observationsByID = useSelector(selectObservationsById)
  //const observation = observationsByID[observationId]

  // TODO: Whenever filter changes, and periodically in the background, pull
  // the index of observation IDs matching the current filters.
  const location = useLocation()
  const navigate = useNavigate()
  const urlParams = new URLSearchParams(location.search)
  const filter = parseObservationFilters(urlParams)
  const dispatch = useDispatch()
  const loc = useSelector((state) =>
    selectLocationWithinIDs(state, observationId)
  )
  const { token } = useSelector(selectAuth)

  // Fetch index every time filter changes
  useEffect(() => {
    dispatch(fetchObservationIDs({ filter }))
  }, [urlParams.toString()])

  // Also fetch index in background (polling) in order to allow user to
  // navigate to new recordings as they come in
  useInterval(
    () => {
      dispatch(fetchObservationIDs({ filter }))
    },
    LIVE_UPDATE_INTERVAL,
    false // Don't run immediately
  )

  // Pre-fetch items the user is most likely to navigate
  // to next which have not been fetched yet.
  const unfetched = useSelector((state) =>
    selectUnfetchedPeers(state, observationId)
  )
  useEffect(() => {
    // Fetch structured data
    unfetched.forEach((id, i) =>
      dispatch(fetchObservationById(id)).then((o) => {
        // Best-effort pre-fetch media files (images only)
        // Note: Only for the next observation, as the current back-end is
        // incredibly slow.
        // TODO: Fetch either previous, or next, depending on direction
        // of navigation!
        const isVeryNext = i == 0
        if (isVeryNext && o?.payload?.derived_files) {
          //console.log("Pre-fetching images for", id)
          o?.payload?.derived_files.filter(isImage).forEach((file) => {
            preFetchFile(id, file, token)
          })
        }
      })
    )
    // TODO: Pre-fetch some of the files? Star map + wiggle?
  }, [loc.prevID, loc.nextID])

  const onBack = () => {
    if (!loc.prevID) {
      return
    }
    navigate({
      pathname: `/observations/${loc.prevID}`,
      search: location.search,
    })
  }

  const onForward = () => {
    if (!loc.nextID) {
      return
    }
    navigate({
      pathname: `/observations/${loc.nextID}`,
      search: location.search,
    })
  }

  // Keyboard navigation
  const leftPress = useKeyPress("ArrowLeft")
  const rightPress = useKeyPress("ArrowRight")
  useEffect(() => (leftPress ? onBack() : undefined), [leftPress])
  useEffect(() => (rightPress ? onForward() : undefined), [rightPress])

  // Link back to all observations matching filter
  const params = new URLSearchParams()
  updateSearchParamsWithFilters(params, filter)
  const obsLink = `/observations?${params.toString()}`

  // TODO: Display errors if any
  return (
    <Bar>
      <Heading>
        Browsing <Link to={obsLink}>current filtered list</Link>:
      </Heading>
      {(loc._loadStatus == "idle" || loc.count > 0) && (
        <NavContainer>
          {loc.prevID && <span onClick={onBack}>◀</span>}
          <span>{loc.pos + 1}</span>
          <span>/</span>
          <span>
            {loc.count}
            {loc.count == 999 ? "+" : ""}
          </span>
          {loc.nextID && <span onClick={onForward}>▶</span>}
        </NavContainer>
      )}
    </Bar>
  )
}

NavigateWithinObservationsList.propTypes = {
  observationId: PropTypes.string.isRequired,
}
export default NavigateWithinObservationsList
